import { useMemo, useState } from 'react';
import { Download, LayoutGrid, Table } from 'lucide-react';
import {
  Button,
  SegmentedControl,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@vnetwork/ui/primitives';
import { Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, Pagination } from '@vnetwork/ui/data';
import {
  useCustomerRevenueCombined,
  exportCustomerRevenueCombined,
  type RevenueCombinedRow,
} from '@/api/customer-revenue';
import { paginationView } from '../pagination-summary';
import { RevenueAllocationStatCards } from './revenue-allocation-stat-cards';
import { detailColumns, overviewColumns, type OverviewGroupRow } from './revenue-allocation-columns';
import { TabError, TabLoading } from './tab-states';

const PAGE_SIZE = 50;

/** Build selectable month options for the last N months up through today (newest first). */
function buildMonthOptions(count = 36): { value: string; label: string }[] {
  const now = new Date();
  const options: { value: string; label: string }[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    options.push({
      value: ym,
      label: d.toLocaleString('en', { month: 'short', year: 'numeric' }),
    });
  }
  return options;
}

const MONTH_OPTIONS = buildMonthOptions(36);

/** Default gte = start of current quarter; lte = current month. Uses local date math (no UTC shift). */
function defaultRange(): { gte: string; lte: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1; // 1-based
  const quarterStartMonth = m - ((m - 1) % 3);
  return {
    gte: `${y}-${String(quarterStartMonth).padStart(2, '0')}`,
    lte: `${y}-${String(m).padStart(2, '0')}`,
  };
}

type ViewMode = 'overview' | 'detail';

/** Aggregate flat rows by product_group_name for the Overview pivot view. */
function groupByProductGroup(rows: RevenueCombinedRow[]): OverviewGroupRow[] {
  const map = new Map<string, { allocated: number; actual: number; projected: number }>();
  for (const r of rows) {
    const key = r.product_group_name ?? '—';
    const existing = map.get(key) ?? { allocated: 0, actual: 0, projected: 0 };
    existing.allocated += r.allocated_amount ?? 0;
    existing.actual += r.revenue_amount ?? r.total_amount ?? 0;
    existing.projected += r.is_projected ? (r.total_amount ?? 0) : 0;
    map.set(key, existing);
  }
  return Array.from(map.entries()).map(([name, v]) => ({ name, ...v }));
}

interface RevenueAllocationTabProps {
  id: number;
}

/**
 * Revenue Allocation tab — month-range filter (two @ui Select controls for gte/lte),
 * Overview/Detail toggle via SegmentedControl, 5 summary stat cards, and a DataTable.
 * Overview groups rows client-side by product_group_name; Detail shows flat paginated rows.
 *
 * API: GET /reports/bi/revenue-combined/customer/:id?gte=YYYY-MM&lte=YYYY-MM&...
 * Row + summary shape confirmed via live probe (all summary fields probed at 0 for test IDs).
 */
export function RevenueAllocationTab({ id }: RevenueAllocationTabProps) {
  const { gte: defaultGte, lte: defaultLte } = useMemo(() => defaultRange(), []);
  const [gte, setGte] = useState(defaultGte);
  const [lte, setLte] = useState(defaultLte);
  const [viewMode, setViewMode] = useState<ViewMode>('overview');
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);

  // Overview fetches all rows (up to 5000) for client-side grouping;
  // Detail uses server-side pagination.
  const params = useMemo(
    () =>
      viewMode === 'overview'
        ? { gte, lte, page: 1, page_size: 5000, sort_by: 'product_group_name', sort_order: 'asc' as const }
        : { gte, lte, page, page_size: PAGE_SIZE, sort_by: 'revenue_month', sort_order: 'desc' as const },
    [gte, lte, viewMode, page],
  );

  const { data, isLoading, isError, refetch } = useCustomerRevenueCombined(id, params);

  const summary = data?.summary;
  const rows = data?.results ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;

  const overviewGroups = useMemo(
    () => (viewMode === 'overview' ? groupByProductGroup(rows) : []),
    [rows, viewMode],
  );

  const handleGteChange = (val: string) => { setGte(val); setPage(1); };
  const handleLteChange = (val: string) => { setLte(val); setPage(1); };
  const handleViewChange = (val: string) => { setViewMode(val as ViewMode); setPage(1); };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      await exportCustomerRevenueCombined(id, { gte, lte });
    } catch {
      // Export errors are silent here; no toast dependency added to this tab
    } finally {
      setIsExporting(false);
    }
  };

  if (isError) {
    return <TabError title="Failed to load revenue allocation" onRetry={() => refetch()} />;
  }

  const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Toolbar
        style={{ marginBottom: 0 }}
        start={
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            <Select value={gte} onValueChange={handleGteChange}>
              <SelectTrigger style={{ minWidth: 140 }}>
                <SelectValue placeholder="From month" />
              </SelectTrigger>
              <SelectContent>
                {MONTH_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
              to
            </span>
            <Select value={lte} onValueChange={handleLteChange}>
              <SelectTrigger style={{ minWidth: 140 }}>
                <SelectValue placeholder="To month" />
              </SelectTrigger>
              <SelectContent>
                {MONTH_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
        end={
          <>
            <SegmentedControl
              size="sm"
              aria-label="View mode"
              items={[
                { value: 'overview', label: 'Overview', icon: <LayoutGrid size={14} /> },
                { value: 'detail', label: 'Detail', icon: <Table size={14} /> },
              ]}
              value={viewMode}
              onValueChange={handleViewChange}
            />
            <Button
              variant="secondary"
              size="sm"
              startIcon={<Download size={15} />}
              disabled={isExporting}
              onClick={handleExport}
            >
              {isExporting ? 'Exporting…' : 'Export'}
            </Button>
          </>
        }
      />

      <RevenueAllocationStatCards summary={summary} />

      {isLoading ? (
        <TabLoading />
      ) : viewMode === 'overview' ? (
        <DataTable
          columns={overviewColumns}
          data={overviewGroups}
          enableSorting={false}
          getRowId={(r) => r.name}
          emptyState={
            <EmptyState
              title="No revenue data"
              description="No revenue allocation records found for the selected period."
            />
          }
        />
      ) : (
        <DataTable
          columns={detailColumns}
          data={rows}
          enableSorting={false}
          getRowId={(r) =>
            String(r.id ?? `${r.invoice_id ?? 'proj'}-${r.revenue_month}-${r.service_id}`)
          }
          emptyState={
            <EmptyState
              title="No revenue data"
              description="No revenue allocation records found for the selected period."
            />
          }
          footer={
            <Pagination
              page={page}
              pageCount={view.pageCount}
              onPageChange={setPage}
              summary={view.summary}
            />
          }
        />
      )}
    </div>
  );
}
